import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Anomalia } from '../api';
import { etiquetaDeCanal } from '../canales';
import { CampanaIcono } from './CampanaIcono';

const TITULO_METRICA: Record<string, string> = {
  tasa_error: 'Errores',
  tasa_abandono: 'Abandono',
  latencia_p95: 'Latencia',
  vital_p75: 'Rendimiento',
};

/**
 * La campana de alertas activas, en el encabezado, visible desde cualquier
 * pestaña.
 *
 * El panel de anomalías (`PanelAnomalias`) solo vive dentro de Procesos, así
 * que alguien mirando Análisis o Releases no se entera de nada hasta que
 * vuelve ahí. La campana pide lo mismo por su cuenta y muestra un número: sin
 * alertas activas, no se nota —ni insignia ni color—, para no entrenar a
 * mirarla siempre igual que la tarjeta vacía de `PanelAnomalias`.
 */
export function CampanaAlertas({
  recarga,
  onVerProceso,
}: {
  /** Cambia cada vez que la vista se recarga. Solo se usa para volver a pedir. */
  recarga: number;
  onVerProceso: (proceso: string) => void;
}) {
  const [anomalias, setAnomalias] = useState<Anomalia[] | null>(null);
  const [abierta, setAbierta] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  const cargar = useCallback(async () => {
    try {
      setAnomalias((await api.anomalias()).anomalias);
    } catch {
      // Igual que en el panel: que falle la detección no puede quebrar el
      // resto del encabezado.
      setAnomalias([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar, recarga]);

  useEffect(() => {
    if (!abierta) return;
    const alHacerClicFuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierta(false);
    };
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierta(false);
    };
    document.addEventListener('mousedown', alHacerClicFuera);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alHacerClicFuera);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierta]);

  const hay = (anomalias?.length ?? 0) > 0;
  const graves = anomalias?.some((a) => a.severidad === 'alta') ?? false;

  return (
    <div className="campana" ref={contenedor}>
      <button
        className={`icono${abierta ? ' activo' : ''}`}
        onClick={() => setAbierta((a) => !a)}
        aria-expanded={abierta}
        aria-haspopup="true"
        title={hay ? `${anomalias?.length} alerta${anomalias?.length === 1 ? '' : 's'} activa${anomalias?.length === 1 ? '' : 's'}` : 'Sin alertas activas'}
      >
        <CampanaIcono />
        {hay && (
          <span className={`campana-insignia${graves ? ' grave' : ''}`}>{anomalias?.length}</span>
        )}
      </button>
      {abierta && (
        <div className="campana-lista" role="menu">
          {!anomalias || anomalias.length === 0 ? (
            <p className="vacio">Sin alertas activas.</p>
          ) : (
            anomalias.map((a, i) => (
              <button
                key={`${a.ambito}-${a.metrica}-${i}`}
                role="menuitem"
                className={`campana-item ${a.severidad}`}
                onClick={() => {
                  setAbierta(false);
                  onVerProceso(a.ambito);
                }}
              >
                <span className="campana-item-cabecera">
                  <span className="etiqueta-metrica">{TITULO_METRICA[a.metrica] ?? a.metrica}</span>
                  <span className="ambito">{a.ambito}</span>
                  <span className={`canal ${a.canal}`}>{etiquetaDeCanal(a.canal)}</span>
                </span>
                <span className="campana-item-texto">{a.explicacion}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
